import React, { useState } from 'react';
import { savePick, deletePick, getLeaguePicksForMatch } from '../services/picksService';
import { getUsernamesMap } from '../services/userService';
import './MatchCard.css';

/* ── Helpers ─────────────────────────────────────────────── */

function formatKickoffEDT(timestamp) {
  if (!timestamp) return '';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp.seconds * 1000);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZoneName: 'short',
  });
}

function pct(odds) {
  if (!odds || isNaN(odds)) return null;
  return Math.round(odds * 100);
}

/* ── Sub-components ──────────────────────────────────────── */

function OddsBar({ match, homeTeam, awayTeam }) {
  const h = pct(match.homeOdds);
  const a = pct(match.awayOdds);
  if (!h || !a) return null;
  return (
    <div className="mc-odds">
      <span className="mc-odds-label">MATCH ODDS</span>
      <div className="mc-odds-bar-wrap">
        <span className="mc-odds-pct mc-odds-pct--home">{h}%</span>
        <div className="mc-odds-bar">
          <div className="mc-odds-bar-home" style={{ width: `${h}%` }} />
        </div>
        <span className="mc-odds-pct mc-odds-pct--away">{a}%</span>
      </div>
      <div className="mc-odds-names">
        <span>{homeTeam?.name ?? '—'}</span>
        <span>{awayTeam?.name ?? '—'}</span>
      </div>
    </div>
  );
}

function StatsPanel({ match, homeTeam, awayTeam, isKnockout, tournamentStats }) {
  const homeTs = tournamentStats?.[match.homeTeamId];
  const awayTs = tournamentStats?.[match.awayTeamId];
  return (
    <div className="mc-stats">
      {match.h2hRecord && (
        match.h2hRecord === 'First Competitive Meeting' ? (
          <div className="mc-stats-row">
            <span className="mc-stats-key">Head to Head</span>
            <span className="mc-stats-val">First Competitive Meeting</span>
          </div>
        ) : (
          <div className="mc-h2h">
            <span className="mc-h2h-name">{homeTeam?.name ?? '—'}</span>
            <span className="mc-h2h-record">{match.h2hRecord}</span>
            <span className="mc-h2h-name mc-h2h-name--away">{awayTeam?.name ?? '—'}</span>
          </div>
        )
      )}
      {homeTeam && (
        <div className="mc-stats-row">
          <span className="mc-stats-key">{homeTeam.name}</span>
          <span className="mc-stats-val">
            {homeTeam.championships ?? 0}x Champion
            {isKnockout && homeTeam.wcKnockoutRecord ? ` · KO: ${homeTeam.wcKnockoutRecord}` : ''}
            {!isKnockout && homeTeam.wcGroupRecord ? ` · GS: ${homeTeam.wcGroupRecord}` : ''}
          </span>
        </div>
      )}
      {awayTeam && (
        <div className="mc-stats-row">
          <span className="mc-stats-key">{awayTeam.name}</span>
          <span className="mc-stats-val">
            {awayTeam.championships ?? 0}x Champion
            {isKnockout && awayTeam.wcKnockoutRecord ? ` · KO: ${awayTeam.wcKnockoutRecord}` : ''}
            {!isKnockout && awayTeam.wcGroupRecord ? ` · GS: ${awayTeam.wcGroupRecord}` : ''}
          </span>
        </div>
      )}
      {isKnockout && homeTeam && homeTs && (
        <div className="mc-stats-row">
          <span className="mc-stats-key">{homeTeam.name} GF/GA</span>
          <span className="mc-stats-val">{homeTs.gf} GF · {homeTs.ga} GA</span>
        </div>
      )}
      {isKnockout && awayTeam && awayTs && (
        <div className="mc-stats-row">
          <span className="mc-stats-key">{awayTeam.name} GF/GA</span>
          <span className="mc-stats-val">{awayTs.gf} GF · {awayTs.ga} GA</span>
        </div>
      )}
    </div>
  );
}

function PickDistribution({ picks, match, homeTeam, awayTeam, usernamesMap }) {
  const homePicks = picks.filter((p) => p.pickedTeamId === match.homeTeamId);
  const awayPicks = picks.filter((p) => p.pickedTeamId === match.awayTeamId);
  const total = picks.length || 1;

  return (
    <div className="mc-dist">
      <div className="mc-dist-header">
        <div className="mc-dist-col-head">
          {homeTeam?.flagCode && <span className={`fi fi-${homeTeam.flagCode}`} />} {homeTeam?.name} ({Math.round((homePicks.length / total) * 100)}%)
        </div>
        <div className="mc-dist-col-head mc-dist-col-head--away">
          ({Math.round((awayPicks.length / total) * 100)}%) {awayTeam?.flagCode && <span className={`fi fi-${awayTeam.flagCode}`} />} {awayTeam?.name}
        </div>
      </div>
      <div className="mc-dist-bar-wrap">
        <div
          className="mc-dist-bar-home"
          style={{ width: `${Math.round((homePicks.length / total) * 100)}%` }}
        />
      </div>
      <div className="mc-dist-cols">
        <div className="mc-dist-list">
          {homePicks.map((p) => (
            <span key={p.id} className="mc-dist-name">{usernamesMap?.[p.userId] ?? p.userId.slice(0, 6)}</span>
          ))}
          {homePicks.length === 0 && <span className="mc-dist-none">None</span>}
        </div>
        <div className="mc-dist-list mc-dist-list--away">
          {awayPicks.map((p) => (
            <span key={p.id} className="mc-dist-name">{usernamesMap?.[p.userId] ?? p.userId.slice(0, 6)}</span>
          ))}
          {awayPicks.length === 0 && <span className="mc-dist-none">None</span>}
        </div>
      </div>
    </div>
  );
}

/* ── MatchCard ───────────────────────────────────────────── */

export default function MatchCard({ match, homeTeam, awayTeam, userPick, roundStatus, leagueId, uid, isKnockout, tournamentStats }) {
  const [statsOpen, setStatsOpen]         = useState(false);
  const [distOpen, setDistOpen]           = useState(false);
  const [distPicks, setDistPicks]         = useState(null);
  const [distUsernames, setDistUsernames] = useState(null);
  const [saving, setSaving]               = useState(false);
  const [pickError, setPickError]         = useState('');

  const canPick  = roundStatus === 'open' && match.status !== 'final' && match.bothTeamsConfirmed !== false;
  const postLock = roundStatus === 'locked' || roundStatus === 'complete';
  const picked   = userPick?.pickedTeamId ?? null;

  async function handlePick(teamId) {
    if (!canPick || saving) return;
    setSaving(true);
    setPickError('');
    try {
      if (picked === teamId) {
        await deletePick(leagueId, uid, match.id);
      } else {
        await savePick(leagueId, uid, match.id, teamId, match.round);
      }
    } catch (_) {
      setPickError('Failed to save — please try again.');
    } finally {
      setSaving(false);
    }
  }

  async function toggleDist() {
    if (!distOpen && !distPicks) {
      try {
        const picks = await getLeaguePicksForMatch(leagueId, match.id);
        const userIds = [...new Set(picks.map((p) => p.userId))];
        const usernamesMap = await getUsernamesMap(userIds);
        setDistPicks(picks);
        setDistUsernames(usernamesMap);
      } catch (_) {
        return;
      }
    }
    setDistOpen((v) => !v);
  }

  const isFinal  = match.status === 'final';
  const isLive   = match.status === 'live';
  const isTbd    = isKnockout && match.bothTeamsConfirmed === false;

  return (
    <div className={`match-card${isFinal ? ' match-card--final' : ''}${isLive ? ' match-card--live' : ''}${isTbd ? ' match-card--tbd' : ''}`}>
      {/* Live badge */}
      {isLive && <div className="match-card-live-badge">LIVE</div>}

      {/* Pick row */}
      <div className="match-card-pick-row">
        {/* Home team */}
        <div
          className={`match-card-team${picked === match.homeTeamId ? ' selected' : ''}${canPick ? ' pickable' : ''}`}
          onClick={() => handlePick(match.homeTeamId)}
          role={canPick ? 'button' : undefined}
          tabIndex={canPick ? 0 : undefined}
          onKeyDown={(e) => e.key === 'Enter' && handlePick(match.homeTeamId)}
        >
          <span className="match-card-flag">{homeTeam?.flagCode ? <span className={`fi fi-${homeTeam.flagCode}`} /> : null}</span>
          <span className="match-card-name">{homeTeam?.name ?? '…'}</span>
          {picked === match.homeTeamId && <span className="match-card-check">✓</span>}
        </div>

        {/* Center */}
        <div className="match-card-center">
          {postLock && isFinal ? (
            <span className="match-card-score">{match.homeScore} – {match.awayScore}</span>
          ) : postLock && isLive ? (
            <span className="match-card-score match-card-score--live">{match.homeScore ?? 0} – {match.awayScore ?? 0}</span>
          ) : (
            <span className="match-card-time">{formatKickoffEDT(match.kickoffTime)}</span>
          )}
        </div>

        {/* Away team */}
        <div
          className={`match-card-team match-card-team--away${picked === match.awayTeamId ? ' selected' : ''}${canPick ? ' pickable' : ''}`}
          onClick={() => handlePick(match.awayTeamId)}
          role={canPick ? 'button' : undefined}
          tabIndex={canPick ? 0 : undefined}
          onKeyDown={(e) => e.key === 'Enter' && handlePick(match.awayTeamId)}
        >
          <span className="match-card-flag">{awayTeam?.flagCode ? <span className={`fi fi-${awayTeam.flagCode}`} /> : null}</span>
          <span className="match-card-name">{awayTeam?.name ?? '…'}</span>
          {picked === match.awayTeamId && <span className="match-card-check">✓</span>}
        </div>
      </div>

      {pickError && <div className="mc-pick-error">{pickError}</div>}

      {/* Odds bar */}
      <OddsBar match={match} homeTeam={homeTeam} awayTeam={awayTeam} />

      {/* Footer: chevron buttons */}
      <div className="match-card-footer">
        <button
          className={`match-card-chevron${statsOpen ? ' open' : ''}`}
          onClick={() => setStatsOpen((v) => !v)}
        >
          Stats {statsOpen ? '▲' : '▼'}
        </button>
        {postLock && (
          <button
            className={`match-card-chevron${distOpen ? ' open' : ''}`}
            onClick={toggleDist}
          >
            Picks {distOpen ? '▲' : '▼'}
          </button>
        )}
      </div>

      {/* Stats panel */}
      {statsOpen && <StatsPanel match={match} homeTeam={homeTeam} awayTeam={awayTeam} isKnockout={isKnockout} tournamentStats={tournamentStats} />}

      {/* Pick distribution (post-lock) */}
      {postLock && distOpen && distPicks && (
        <PickDistribution picks={distPicks} match={match} homeTeam={homeTeam} awayTeam={awayTeam} usernamesMap={distUsernames} />
      )}
    </div>
  );
}
